import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import {
  IndianRupee, Wallet, Home, BarChart3, Target,
  ArrowUpRight, ArrowDownRight, AlertTriangle,
  CheckCircle2, TrendingUp, Building2, ShieldAlert,
  FileText, Sparkles, ChevronDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from "recharts";
import SummaryTables from "@/components/SummaryTables";
import ExpectedCashflow from "@/components/ExpectedCashflow";
import { PeriodBadge } from "@/components/Formatters";


const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(num) {
  if (!num && num !== 0) return "--";
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)} L`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return sign + abs.toLocaleString();
}

const PERIODS = [
  { key: "mtd", label: "MTD" },
  { key: "qtd", label: "QTD" },
  { key: "ytd", label: "YTD" },
  { key: "as_on_date", label: "As On Date" },
];

function ExecKPI({ title, value, subtitle, icon: Icon, color, trend, changePct, delay = 0 }) {
  const hasChange = changePct != null && changePct !== 0;
  const changeUp = changePct > 0;
  return (
    <div className="exec-kpi-card fade-in" style={{ animationDelay: `${delay}s` }}
      data-testid={`exec-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
          <div className="exec-kpi-icon flex-shrink-0" style={{ background: `${color}10`, color }}>
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <span className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 leading-none whitespace-nowrap">{title}</span>
        </div>
        {hasChange && (
          <span className={`text-[9px] sm:text-xs font-bold flex items-center gap-0.5 flex-shrink-0 ml-1 ${changeUp ? 'text-emerald-600' : 'text-red-600'}`}>
            {changeUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(changePct)}%
          </span>
        )}
      </div>
      <p className="exec-kpi-value" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      {subtitle && (
        <p className="text-[9px] sm:text-xs text-slate-400 mt-1 sm:mt-2 leading-tight line-clamp-2">{subtitle}</p>
      )}
    </div>
  );
}

function AlertItem({ alert }) {
  const colorMap = {
    danger: { bg: "bg-red-50", border: "border-red-100", text: "text-red-700", icon: ShieldAlert, iconColor: "text-red-500" },
    warning: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700", icon: AlertTriangle, iconColor: "text-amber-500" },
    success: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-700", icon: CheckCircle2, iconColor: "text-emerald-500" },
  };
  const style = colorMap[alert.type] || colorMap.warning;
  const AlertIcon = style.icon;
  return (
    <div className={`flex items-start gap-3 p-3 sm:p-3.5 rounded-xl ${style.bg} border ${style.border}`}>
      <AlertIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${style.text}`}>{alert.category}</span>
          {alert.value != null && <span className={`text-xs font-bold ${style.text}`}>{alert.value}%</span>}
        </div>
        <p className="text-xs sm:text-sm text-slate-700 mt-0.5">{alert.message}</p>
      </div>
    </div>
  );
}

export default function ExecutiveCommand() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/executive`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      
      // If API returns but has no data, retry once after 3s (sync might be in progress)
      if (!res.data.has_data && !window._kpiRetried) {
        window._kpiRetried = true;
        setTimeout(async () => {
          try {
            const retry = await axios.get(`${API}/kpi/executive`, {
              headers: { Authorization: `Bearer ${token}` },
              params,
            });
            if (retry.data.has_data) {
              setData(retry.data);
            }
          } catch(retryErr) { console.error("KPI retry failed:", retryErr); }
          window._kpiRetried = false;
        }, 3000);
      } else {
        window._kpiRetried = false;
      }
      
      setData(res.data);

      // Fetch sync status (lightweight)
      axios.get(`${API}/sync/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const ts = r.data?.last_sync?.timestamp;
          if (ts) setLastSync(ts);
        }).catch(err => { console.error("Sync status fetch failed:", err); });
    } catch (err) {
      console.error("Failed to fetch executive data", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are module-level constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const hasData = d.has_data;
  const chg = d.changes || {};
  const prev = d.prev || {};
  const prevLabel = d.prev_label || 'Prev';

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="executive-command-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Executive Command Center
        </h1>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-0.5 sm:mt-1">
          <p className="text-xs sm:text-sm text-slate-500">Strategic overview for leadership decision-making</p>
          <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
          {lastSync && (
            <span className="text-[10px] text-slate-400 flex items-center gap-1" data-testid="last-sync-time">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
              Data updated: {new Date(lastSync).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="inline-block h-5 w-5 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {!loading && !hasData && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="no-data-banner">
          <svg className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">No data available for the selected period</p>
            <p className="text-xs text-amber-600 mt-1">Data sync may be in progress or there's no transaction data for this month. Try selecting a different month from the dropdown above, or wait a moment and refresh.</p>
            <button onClick={fetchData} className="mt-2 text-xs font-medium text-amber-700 underline hover:text-amber-900" data-testid="retry-data-btn">Retry now</button>
          </div>
        </div>
      )}

      {/* Row 1: Sales — Target, Gross Sales, Cancellation, Net Sales */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] mb-2 ml-1">Sales</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4" data-testid="exec-kpi-sales">
          <div className="kpi-card fade-in" style={{ animationDelay: '0.05s' }}>
            <div className="flex items-center gap-1.5 sm:gap-2.5 mb-2 sm:mb-3"><div className="exec-kpi-icon flex-shrink-0" style={{ background: '#1D4ED810', color: '#1D4ED8' }}><Target className="h-4 w-4 sm:h-5 sm:w-5" /></div><span className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 whitespace-nowrap">Sales Target</span></div>
            <p className="exec-kpi-value" style={{ fontFamily: 'Manrope' }}>{formatCurrency(d.target_value)}</p>
            <p className="text-[9px] text-slate-400 mt-1 sm:mt-2">Achievement: {d.achievement_pct || 0}%</p>
          </div>
          <div className="kpi-card fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between mb-2 sm:mb-3"><div className="flex items-center gap-1.5 sm:gap-2.5"><div className="exec-kpi-icon flex-shrink-0" style={{ background: '#1D4ED810', color: '#1D4ED8' }}><IndianRupee className="h-4 w-4 sm:h-5 sm:w-5" /></div><span className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 whitespace-nowrap">Gross Sales</span></div>{chg.sales_value !== 0 && <span className={`text-[9px] sm:text-xs font-bold flex items-center gap-0.5 ${chg.sales_value > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{chg.sales_value > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(chg.sales_value || 0)}%</span>}</div>
            <p className="exec-kpi-value" style={{ fontFamily: 'Manrope' }}>{formatCurrency(d.sales_value)}</p>
            <p className="text-[9px] text-slate-400 mt-1 sm:mt-2">{prevLabel}: {formatCurrency(prev.sales_value)}</p>
          </div>
          <ExecKPI title="Cancellations" value={hasData ? formatCurrency(d.cancel_value) : "--"} subtitle={hasData ? `${d.cancel_count || 0} units · ${prevLabel}: ${formatCurrency(prev.cancel_value)}` : ""} icon={ArrowDownRight} color="#EF4444" changePct={chg.cancel_value} delay={0.15} />
          <ExecKPI title="Net Sales" value={hasData ? formatCurrency(d.net_sales) : "--"} subtitle={hasData ? `${prevLabel}: ${formatCurrency(prev.net_sales)}` : ""} icon={IndianRupee} color="#1D4ED8" changePct={chg.net_sales} delay={0.2} />
        </div>
      </div>

      {/* Row 2: Collection — Target, Actual, % */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] mb-2 ml-1">Collection</p>
        <div className="grid grid-cols-3 gap-2.5 sm:gap-4" data-testid="exec-kpi-collection">
          <div className="kpi-card fade-in" style={{ animationDelay: '0.05s' }}>
            <p className="text-[10px] font-semibold text-teal-500 uppercase tracking-wider">Collection Target</p>
            <p className="text-base sm:text-xl font-bold text-teal-600 mt-1" style={{ fontFamily: 'Manrope' }}>{formatCurrency(d.collection_target)}</p>
          </div>
          <div className="kpi-card fade-in" style={{ animationDelay: '0.1s' }}>
            <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Collection</p>
            <p className="text-base sm:text-xl font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Manrope' }}>{formatCurrency(d.collection)}</p>
          </div>
          <div className="kpi-card fade-in" style={{ animationDelay: '0.15s' }}>
            <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">Efficiency</p>
            <p className="text-base sm:text-xl font-bold text-blue-600 mt-1" style={{ fontFamily: 'Manrope' }}>{d.collection_efficiency || 0}%</p>
            <p className="text-[9px] text-slate-400 mt-0.5">of target collected</p>
          </div>
        </div>
      </div>

      {/* Row 3: Count — Sold Units, Cancelled Units, SBA */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] mb-2 ml-1">Count</p>
        <div className="grid grid-cols-3 gap-2.5 sm:gap-4" data-testid="exec-kpi-units">
          <div className="kpi-card fade-in" style={{ animationDelay: '0.05s' }}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Sold Units</p>
            <p className="text-base sm:text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{hasData && d.sales_count > 0 ? d.sales_count.toLocaleString() : '--'}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{prevLabel}: {prev.sales_count || 0}</p>
          </div>
          <div className="kpi-card fade-in" style={{ animationDelay: '0.1s' }}>
            <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider">Cancelled Units</p>
            <p className="text-base sm:text-xl font-bold text-red-600 mt-1" style={{ fontFamily: 'Manrope' }}>{d.cancel_count || 0}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{prevLabel}: {prev.cancel_count || 0}</p>
          </div>
          <div className="kpi-card fade-in" style={{ animationDelay: '0.15s' }}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">SBA (Net)</p>
            <p className="text-base sm:text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{d.net_sba ? `${d.net_sba.toLocaleString('en-IN')}` : '--'} <span className="text-xs font-normal text-slate-400">Sq.ft</span></p>
            <p className="text-[9px] text-slate-400 mt-0.5">{prevLabel}: {prev.net_sba ? prev.net_sba.toLocaleString('en-IN') : '--'}</p>
          </div>
        </div>
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="exec-sales-trend">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Revenue Trend</CardTitle></div>
              {d.granularity && <span className="text-[9px] text-slate-400 uppercase font-medium">{d.granularity === 'day' ? 'Daily' : d.granularity === 'week' ? 'Weekly' : 'Monthly'}</span>}
            </div>
          </CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {d.sales_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={d.sales_trend} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#94A3B8' }} angle={d.granularity === 'day' ? -45 : 0} textAnchor={d.granularity === 'day' ? 'end' : 'middle'} height={d.granularity === 'day' ? 50 : 30} interval={d.granularity === 'day' ? 2 : 0} />
                  <YAxis tick={{ fontSize: 9, fill: '#94A3B8' }} tickFormatter={formatCurrency} width={50} />
                  <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v) => [formatCurrency(v), "Revenue"]} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {d.sales_trend.map((entry) => <Cell key={entry.month} fill={entry.in_period ? '#1D4ED8' : '#CBD5E1'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (<div className="flex items-center justify-center h-[220px] text-slate-300"><TrendingUp className="h-8 w-8 mx-auto opacity-40" /></div>)}
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="exec-collection-trend">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-emerald-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collection Trend</CardTitle></div>
              {d.granularity && <span className="text-[9px] text-slate-400 uppercase font-medium">{d.granularity === 'day' ? 'Daily' : d.granularity === 'week' ? 'Weekly' : 'Monthly'}</span>}
            </div>
          </CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {d.collection_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={d.collection_trend} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#94A3B8' }} angle={d.granularity === 'day' ? -45 : 0} textAnchor={d.granularity === 'day' ? 'end' : 'middle'} height={d.granularity === 'day' ? 50 : 30} interval={d.granularity === 'day' ? 2 : 0} />
                  <YAxis tick={{ fontSize: 9, fill: '#94A3B8' }} tickFormatter={formatCurrency} width={50} />
                  <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v) => [formatCurrency(v), "Collection"]} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {d.collection_trend.map((entry) => <Cell key={entry.month} fill={entry.in_period ? '#10B981' : '#CBD5E1'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (<div className="flex items-center justify-center h-[220px] text-slate-300"><Wallet className="h-8 w-8 mx-auto opacity-40" /></div>)}
          </CardContent>
        </Card>
      </div>

      {/* Summary Tables */}
      <SummaryTables token={token} period={period} refDate={refDate} />

      {/* Expected Cashflow Forecast */}
      <ExpectedCashflow token={token} refDate={refDate} />

      {/* Project Rankings — with visual bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="exec-top-projects">
          <CardHeader className="pb-3 border-b border-slate-100"><div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Revenue by Project</CardTitle></div></CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {d.sales_by_project?.length > 0 ? (
              <div className="space-y-3">
                {d.sales_by_project.slice(0, 8).map((proj, i) => {
                  const maxVal = d.sales_by_project[0]?.value || 1;
                  const pct = Math.round((proj.value / maxVal) * 100);
                  return (
                    <div key={i} data-testid={`top-project-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-slate-700 truncate flex-1 mr-2">{proj.name}</span>
                        <span className="text-[11px] font-bold text-slate-900 flex-shrink-0" style={{ fontFamily: 'Manrope' }}>{formatCurrency(proj.value)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `hsl(${220 - i * 12}, 70%, ${50 + i * 3}%)` }} />
                      </div>
                      <p className="text-[9px] text-slate-400 mt-0.5">{proj.count} units · {proj.sba?.toLocaleString('en-IN')} Sq.ft</p>
                    </div>
                  );
                })}
              </div>
            ) : (<div className="text-center py-6"><Building2 className="h-8 w-8 text-slate-200 mx-auto mb-2" /><p className="text-xs text-slate-400">No sales data</p></div>)}
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="exec-collection-projects">
          <CardHeader className="pb-3 border-b border-slate-100"><div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-emerald-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collection by Project</CardTitle></div></CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            {d.collection_by_project?.length > 0 ? (
              <div className="space-y-3">
                {d.collection_by_project.slice(0, 8).map((proj, i) => {
                  const maxVal = d.collection_by_project[0]?.value || 1;
                  const pct = Math.round((proj.value / maxVal) * 100);
                  return (
                    <div key={i} data-testid={`coll-project-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-slate-700 truncate flex-1 mr-2">{proj.name}</span>
                        <span className="text-[11px] font-bold text-emerald-700 flex-shrink-0" style={{ fontFamily: 'Manrope' }}>{formatCurrency(proj.value)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `hsl(${155 - i * 8}, 65%, ${40 + i * 4}%)` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (<div className="text-center py-6"><Building2 className="h-8 w-8 text-slate-200 mx-auto mb-2" /><p className="text-xs text-slate-400">No collection data</p></div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
